import React, {
  FC,
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import _debounce from 'lodash/debounce';
import { bindTrigger } from 'material-ui-popup-state/hooks';
import * as yup from 'yup';

import { TalentQuestionsResponseDTO } from '@/api/generated';
import ChevronDown from '@/assets/icons/chevron-down.svg?react';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import StandardPopper from '@/components/StandardPopper/StandardPopper';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import { useWarnings } from '@/hooks/useWarnings';
import { borders, transforms, transitions } from '@/theme/constants';
import typography from '@/theme/typography';

import { DEFAULT_LANGUAGE_CODE } from './constants';

const MIN_WIDTH = 260;

interface LanguageListSectionProps {
  options: string[];
  value: string[];
  selectedValue: string;
  onChange: (languageCodes: string[]) => void;
  onSelectedChange: (languageCode: string) => void;
  validation: yup.Schema<TalentQuestionsResponseDTO>;
}

const LanguageListSection: FC<LanguageListSectionProps> = ({
  value,
  selectedValue,
  onChange,
  onSelectedChange,
  options,
  validation
}) => {
  const { getPrefixedFieldName, getPrefixedFieldValue } = useFieldPrefix();
  const { t } = useTranslation(['common', 'settings']);
  const { spacing, palette } = useTheme();
  const popperRef = useRef();
  const warningsContext = useWarnings();
  const languageWarningsSet = useMemo(() => {
    const warningsList = Object.keys(warningsContext?.warnings || {});
    const languageWarnings = options.filter(language =>
      warningsList.some((warningPath: string) =>
        warningPath.startsWith(getPrefixedFieldName(`translations.${language}.`))
      )
    );

    return new Set(languageWarnings);
  }, [warningsContext?.warnings, options, getPrefixedFieldName]);

  const [languageErrors, setLanguageErrors] = useState<string[]>([]);
  const languageErrorsSet = useMemo(() => new Set(languageErrors), [languageErrors]);

  const { watch } = useFormContext();

  const validateLanguages = useCallback(
    (value: FieldValues) => {
      validation
        .validate(value, { abortEarly: false })
        .then(() => setLanguageErrors([]))
        .catch(error => {
          const { errors } = error;
          const languageErrors = options.filter(language =>
            (errors || []).some((errorPath: string) => errorPath.startsWith(`translations.${language}.`))
          );
          setLanguageErrors(languageErrors);
        });
    },
    [options, validation]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onDebouncedValidate = useCallback(_debounce(validateLanguages!, 100), [validateLanguages]);

  useEffect(() => {
    const subscription = watch((value: FieldValues) => onDebouncedValidate(getPrefixedFieldValue(value)));

    return () => subscription.unsubscribe();
  }, [options, validation, watch, onDebouncedValidate, getPrefixedFieldValue]);

  const languageOptions = useMemo(
    () =>
      options
        .filter(option => !value?.includes(option))
        .map(language => ({ value: language, label: t(`common.languages.${language}`) })),
    [options, value, t]
  );

  const onAddLanguage = useCallback(
    (newLanguage: string | string[]) => onChange([...value, newLanguage as string]),
    [onChange, value]
  );

  const onRemoveLanguage = useCallback(
    (removedLanguage: string) => {
      onChange?.(value?.filter(languageOption => languageOption !== removedLanguage));
    },
    [onChange, value]
  );

  const handleClose = useCallback(() => (popperRef.current! as { close?: () => void })?.close?.(), []);

  const handleClickAndClose = useCallback(
    (onClick: undefined | ((e: ReactMouseEvent<HTMLElement, MouseEvent>) => void)) =>
      (e: ReactMouseEvent<HTMLElement, MouseEvent>) => {
        handleClose();
        onClick?.(e);
      },
    [handleClose]
  );

  useEffect(() => {
    if (!value?.includes(selectedValue)) {
      onSelectedChange?.(DEFAULT_LANGUAGE_CODE);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormControl>
      <FormLabel
        component="h3"
        sx={{
          ...typography.body3,
          fontWeight: typography.fontWeightMedium,
          lineHeight: spacing(2),
          color: palette.grey[700],
          textAlign: 'start',
          mb: 2
        }}
      >
        {t('common.languages.list')}
      </FormLabel>
      <Stack minWidth={MIN_WIDTH} gap={2}>
        {value?.map((language: string) => {
          const isSelected = language === selectedValue;

          return (
            <Stack
              direction="row"
              justifyContent="space-between"
              key={language}
              sx={{ bgcolor: isSelected ? 'primary.100' : 'transparent', cursor: 'pointer' }}
              onClick={() => onSelectedChange(language)}
              p={spacing(1.25, 1)}
            >
              <Typography direction="row" component={Stack} sx={typography.body2} alignItems="center" flexGrow={1}>
                {t(`common.languages.${language}`)}
              </Typography>
              <Stack direction="row" justifyContent="center" alignItems="center" gap={0.5}>
                {(languageErrorsSet.has(language) || languageWarningsSet.has(language)) && (
                  <Box
                    mx={0.5}
                    sx={{
                      width: spacing(1),
                      height: spacing(1),
                      borderRadius: spacing(1),
                      bgcolor: languageErrorsSet.has(language) ? 'red.main' : 'orange.main'
                    }}
                  />
                )}
                <IconButton
                  title={t('settings:orgSettings.talentRegistrationForm.delete')}
                  onClick={() => onRemoveLanguage(language)}
                  sx={{
                    p: 0,
                    visibility: language !== DEFAULT_LANGUAGE_CODE ? 'visible' : 'hidden',
                    '& .DeleteIcon-root': {
                      color: 'grey.600'
                    }
                  }}
                >
                  <DeleteIcon className="DeleteIcon-root" />
                </IconButton>
              </Stack>
            </Stack>
          );
        })}
        {!!languageOptions.length && (
          <StandardPopper
            getSx={() => ({ zIndex: 2000 })}
            ref={popperRef}
            treeRootSelector=".MuiDialog-container"
            content={
              <Paper
                sx={{
                  mt: 1,
                  p: 0,
                  position: 'relative',
                  width: MIN_WIDTH,
                  '.StandardPopper-root &': {
                    bgcolor: palette.background.default
                  },
                  '.StandardPopper-root &.MuiPaper-root::before, .StandardPopper-root &.MuiPaper-root::after': {
                    display: 'none'
                  }
                }}
              >
                <MenuList sx={{ py: 0 }}>
                  {languageOptions.map(({ value, label }) => {
                    const onClick = () => onAddLanguage(value);

                    return (
                      <MenuItem sx={{ p: 0 }} onClick={handleClickAndClose(onClick)} key={value}>
                        <Button
                          component={Stack}
                          direction="row"
                          sx={{
                            border: 'none',
                            width: '100%',
                            bgcolor: 'transparent',
                            color: 'text.primary',
                            textAlign: 'start',
                            justifyContent: 'start',
                            p: spacing(1.5, 2),
                            ...typography.body1
                          }}
                        >
                          {label}
                        </Button>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Paper>
            }
          >
            {popupState => (
              <Button
                {...bindTrigger(popupState)}
                sx={{
                  gap: 1,
                  width: '100%',
                  border: borders.half,
                  borderColor: 'grey.400',
                  pt: 0.75,
                  pb: 1,
                  lineHeight: spacing(2.25)
                }}
              >
                {t('settings:orgSettings.talentRegistrationForm.addLanguage')}
                <Stack
                  justifyContent="center"
                  sx={{
                    '& .ArrowIcon-root': {
                      transform: transforms.mirrorY(popupState.isOpen),
                      transition: transitions.transform
                    }
                  }}
                >
                  <ChevronDown className="ArrowIcon-root" />
                </Stack>
              </Button>
            )}
          </StandardPopper>
        )}
      </Stack>
    </FormControl>
  );
};

export default LanguageListSection;
